import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import HotelStarPricingTab from './PricingTabs/HotelStarPricingTab';
import CityPricingTab from './PricingTabs/CityPricingTab';
import DistancePricingTab from './PricingTabs/DistancePricingTab';
import UrgencyFactorsTab from './PricingTabs/UrgencyFactorsTab';
import GroupDiscountsTab from './PricingTabs/GroupDiscountsTab';
import ServicePricingTab from './PricingTabs/ServicePricingTab';
import ProfitMarginsTab from './PricingTabs/ProfitMarginsTab';
import FormulaComponentsTab from './PricingTabs/FormulaComponentsTab';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PricingConfiguration = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);

  const fetchPricingConfiguration = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/pricing/configuration`);
      setPricingConfig(response.data);
    } catch (err) {
      setError('Failed to load pricing configuration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingConfiguration();
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSuccess(null);
    setError(null);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  if (loading && !pricingConfig) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading pricing configuration...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Settings fontSize="large" color="primary" />
          Pricing Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all pricing factors that affect package calculations. Changes here will be reflected immediately on the user website.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="pricing configuration tabs"
          >
            <Tab label="Hotel Star Pricing" />
            <Tab label="City Pricing" />
            <Tab label="Distance/Transport" />
            <Tab label="Urgency Factors" />
            <Tab label="Group Discounts" />
            <Tab label="Additional Services" />
            <Tab label="Profit Margins" />
            <Tab label="Formula Components" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <HotelStarPricingTab
            data={pricingConfig?.hotelStarPricing || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <CityPricingTab
            data={pricingConfig?.cityPricing || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <DistancePricingTab
            data={pricingConfig?.distancePricing || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <UrgencyFactorsTab
            data={pricingConfig?.urgencyFactors || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <GroupDiscountsTab
            data={pricingConfig?.groupDiscounts || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <ServicePricingTab
            data={pricingConfig?.servicePricing || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={6}>
          <ProfitMarginsTab
            data={pricingConfig?.profitMargins || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={7}>
          <FormulaComponentsTab
            data={pricingConfig?.formulaComponents || []}
            onRefresh={fetchPricingConfiguration}
            onSuccess={showSuccess}
            onError={showError}
          />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default PricingConfiguration;
